/**
 * Session actor identity: MySQL account_user (via GetSelfProfile) preferred,
 * Postgres allData only as fallback / chart-subject context.
 *
 * allData.patient is the chart subject when viewingPatientChart — never treat
 * it as "me" in that mode. Overlay patches manager (actor) when charting and
 * patient+user when not.
 */
import { emptySelfProfile, normalizeSelfProfile } from '@/utils/selfProfileForm';

function str(v) {
  if (v === undefined || v === null) return '';
  return String(v).trim();
}

function hasMysqlSelf(sessionSelf) {
  if (!sessionSelf || typeof sessionSelf !== 'object') return false;
  return str(sessionSelf.unique_ID) !== '' && str(sessionSelf.accountnumber) !== '';
}

function displayNameFrom(first, last, fallback) {
  const name = `${str(first)} ${str(last)}`.trim();
  return name || str(fallback) || '';
}

/** Stable actor contract for privileges, messaging, chrome, etc. */
export function sessionActorFrom(allData, sessionSelf) {
  // If the sessionSelf has a MySQL self, return the MySQL self
  if (hasMysqlSelf(sessionSelf)) {
    const self = normalizeSelfProfile(sessionSelf);
    return {
      accountnumber: self.accountnumber,
      unique_ID: self.unique_ID,
      externId: self.unique_ID,
      externId2: self.accountnumber,
      firstName: self.firstName,
      lastName: self.lastName,
      email: self.email,
      phone1: self.phone1,
      username: self.username,
      displayName: displayNameFrom(self.firstName, self.lastName, self.username),
      source: 'mysql',
    };
  }

  // If the sessionSelf does not have a MySQL self, return the PHR data (old default fallback)
  const phr = allData || {};
  const patient = phr.patient || {};
  const manager = phr.manager || {};
  const charting = !!phr.viewingPatientChart;
  const actor = charting && (str(manager.externid) || str(manager.externId))
    ? manager
    : patient;

  const unique_ID = str(
    actor.externid || actor.externId || patient.externId || patient.externid
  );
  const accountnumber = str(
    actor.externid2 ||
      actor.externId2 ||
      patient.externId2 ||
      patient.externid2
  );
  const firstName = str(actor.firstName || actor.fname);
  const lastName = str(actor.lastName || actor.lname);

  return {
    accountnumber,
    unique_ID,
    externId: unique_ID,
    externId2: accountnumber,
    firstName,
    lastName,
    email: str(actor.email),
    phone1: str(actor.phone1 || actor.cell || actor.home),
    username: str(actor.username || actor.userName),
    displayName: displayNameFrom(
      firstName,
      lastName,
      actor.name || actor.username
    ),
    source: 'phr_fallback',
  };
}

/**
 * Chrome / welcome display name: MySQL sessionSelf only.
 * Never fall back to Postgres allData — that shows a stale name until
 * GetSelfProfile lands (and would stick if MFA blocked the first fetch).
 */
export function chromeDisplayNameFrom(sessionSelf) {
  if (!hasMysqlSelf(sessionSelf)) return '';
  const self = normalizeSelfProfile(sessionSelf);
  return displayNameFrom(self.firstName, self.lastName, self.username);
}

/**
 * Shape used by Floating/Mobile message widgets.
 * Prefers MySQL sessionSelf via sessionActorFrom.
 */
export function messagingIdentityFrom(allData, sessionSelf) {
  const actor = sessionActorFrom(allData, sessionSelf);
  return {
    accountNumber: actor.accountnumber,
    subAccountNumber: actor.unique_ID,
    senderName: actor.displayName || 'Unknown User',
  };
}

// This function is used to patch the bio data from the self profile
function bioPatchFromSelf(self) {
  return {
    firstName: self.firstName,
    lastName: self.lastName,
    email: self.email,
    phone1: self.phone1,
    cell: self.phone1,
    username: self.username,
    dob: self.dob,
    address: self.address,
    city: self.city,
    state: self.state,
    zip: self.zip,
  };
}

/**
 * Compatibility overlay onto a Vuex allData snapshot.
 * Returns a new object; does not mutate. No-op if MySQL self is incomplete.
 */
export function applySessionSelfOverlay(allData, sessionSelf) {
  if (!allData || !hasMysqlSelf(sessionSelf)) {
    return allData;
  }
  const self = normalizeSelfProfile(sessionSelf);
  const bio = bioPatchFromSelf(self);
  const charting = !!allData.viewingPatientChart;

  if (charting) {
    const manager = { ...(allData.manager || {}) };
    Object.assign(manager, bio, {
      externid: self.unique_ID,
      externId: self.unique_ID,
      externid2: self.accountnumber,
      externId2: self.accountnumber,
      name: displayNameFrom(self.firstName, self.lastName, self.username),
    });
    return { ...allData, manager };
  }

  const patient = {
    ...(allData.patient || {}),
    ...bio,
    externId: self.unique_ID,
    externid: self.unique_ID,
    externId2: self.accountnumber,
    externid2: self.accountnumber,
  };
  const user = {
    ...(allData.user || {}),
    userName: self.username || (allData.user && allData.user.userName) || '',
    dob: self.dob || (allData.user && allData.user.dob) || '',
  };
  return { ...allData, patient, user };
}

/** Empty profile shaped for the store. */
export function emptySessionSelf() {
  return emptySelfProfile();
}
